import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/th";
import FileModal from "./FileModal";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from "date-fns/locale/th";

registerLocale("th", th);
moment.locale("th");

const FALLBACK_BANK_OPTIONS = [
  { code: "BAAC", name: "ธ.ก.ส. (BAAC)" },
  { code: "BAY", name: "ธนาคารกรุงศรีอยุธยา (BAY)" },
  { code: "BBL", name: "ธนาคารกรุงเทพ (BBL)" },
  { code: "CIMB", name: "ธนาคารซีไอเอ็มบีไทย (CIMB)" },
  { code: "GHB", name: "ธนาคารอาคารสงเคราะห์ (GHB)" },
  { code: "GSB", name: "ธนาคารออมสิน (GSB)" },
  { code: "KBANK", name: "ธนาคารกสิกรไทย (KBANK)" },
  { code: "KTB", name: "ธนาคารกรุงไทย (KTB)" },
  { code: "LHB", name: "ธนาคารแลนด์ แอนด์ เฮ้าส์ (LHB)" },
  { code: "SCB", name: "ธนาคารไทยพาณิชย์ (SCB)" },
  { code: "TTB", name: "ธนาคารทหารไทยธนชาต (TTB)" },
  { code: "UOB", name: "ธนาคารยูโอบี (UOB)" },
];

const ThaiDateInput = React.forwardRef(({ value, onClick, placeholder, className }, ref) => {
  let display = value;
  if (value) {
    const parts = value.split("/");
    if (parts.length === 3) {
      display = `${parts[0]}/${parts[1]}/${parseInt(parts[2]) + 543}`;
    }
  }
  return (
    <input
      ref={ref}
      value={display || ""}
      onClick={onClick}
      onChange={() => {}}
      className={className}
      placeholder={placeholder || "เลือกวันที่"}
    />
  );
});

const thaiDateHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
  <div className="flex items-center justify-between px-4 py-1">
    <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled} className="text-gray-600 hover:text-gray-800 text-lg font-bold">‹</button>
    <span className="text-sm font-semibold">
      {moment(date).locale("th").format("MMMM")} {moment(date).year() + 543}
    </span>
    <button onClick={increaseMonth} disabled={nextMonthButtonDisabled} className="text-gray-600 hover:text-gray-800 text-lg font-bold">›</button>
  </div>
);

// Format number with commas
function formatWithCommas(numStr) {
  if (!numStr && numStr !== 0) return "";
  const parts = String(numStr).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}

// NumberInput component สำหรับ format number
const NumberInput = ({ value, onValueChange, disabled = false }) => {
  const [localValue, setLocalValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      if (value === null || value === undefined || value === "") {
        setLocalValue("");
      } else {
        setLocalValue(formatWithCommas(parseFloat(value).toFixed(2)));
      }
    }
  }, [value]);

  const handleFocus = (e) => e.target.select();
  
  const handleChange = (e) => {
    let raw = e.target.value.replace(/,/g, "");
    if (!/^\d*\.?\d*$/.test(raw)) return;
    const parts = raw.split(".");
    if (parts[1]?.length > 2) return;
    let displayVal = raw;
    if (parts[0].length > 3) displayVal = formatWithCommas(raw);
    setLocalValue(displayVal);
    const numericValue = parseFloat(raw) || 0;
    onValueChange(numericValue);
  };

  const handleBlur = () => {
    const numericValue = parseFloat(localValue.replace(/,/g, "")) || 0;
    const formatted = formatWithCommas(numericValue.toFixed(2));
    setLocalValue(formatted);
    onValueChange(numericValue);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={localValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      readOnly={disabled}
      className="w-full border rounded px-3 py-2 h-[42px] text-right text-[14px] focus:ring-1 focus:ring-[#E13F57] focus:border-[#E13F57] transition bg-white"
      inputMode="decimal"
      placeholder="0.00"
    />
  );
};

export default function InvoiceEditModal({ isOpen, onClose, invoice, onSuccess }) {
  const [formData, setFormData] = useState({
    Invoice_No: "",
    Paid_Amount: "",
    Paid_Date: "",
    Remark: "",
    Payment_Method: "1",
    Ref1: "",
    Ref2: "",
    Withholding_Tax: false,
    Bank: "",
    Check_No: "",
    Check_Date: "",
    Transfer_Bank: "",
    Transfer_Account: "",
    Card_Bank: "",
    Card_No: "",
  });
  const [paidAmountError, setPaidAmountError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]); // เก็บไฟล์ที่ต้องการลบ (รอให้กดบันทึก)
  const [showViewAttachModal, setShowViewAttachModal] = useState(false);
  const [openUploadAttach, setOpenUploadAttach] = useState(false);
  const [userName, setUserName] = useState("");
  const [bankOptions, setBankOptions] = useState(FALLBACK_BANK_OPTIONS);

  useEffect(() => {
    if (isOpen && invoice) {
      let paidDate = "";
      
      // ถ้ามี PayDate ใช้ PayDate, ถ้าไม่มีใช้ Date (วันที่ออก Inv) เป็น default
      let dateToUse = invoice.PayDate && invoice.PayDate !== "-" ? invoice.PayDate : invoice.Date;
      
      if (dateToUse && dateToUse !== "-") {
        // Parse from DD/MM/YYYY format (ปี พ.ศ.)
        const parts = dateToUse.split("/");
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          let yearBE = parseInt(parts[2], 10);
          
          // ถ้าปีเป็น พ.ศ. (มากกว่า 2500) แปลงเป็น ค.ศ.
          if (yearBE > 2500) {
            yearBE = yearBE - 543;
          }
          
          const parsed = moment(`${yearBE}-${month}-${day}`, "YYYY-MM-DD");
          if (parsed.isValid()) {
            // เก็บเป็น ค.ศ. ใน state (YYYY-MM-DD)
            paidDate = parsed.format("YYYY-MM-DD");
          }
        }
      }
      
      // ถ้าไม่มีวันที่เลย ใช้วันที่ปัจจุบัน
      if (!paidDate) {
        paidDate = moment().format("YYYY-MM-DD");
      }
      
      const normalizeBankCode = (v) => {
        if (!v || v === "-") return "";
        const s = String(v).trim().toUpperCase();
        if (s === "BAAC") return "BAAC";
        if (s === "BAY") return "BAY";
        if (s === "BBL") return "BBL";
        if (s === "CIMB") return "CIMB";
        if (s === "GHB") return "GHB";
        if (s === "GSB") return "GSB";
        if (s === "KBANK") return "KBANK";
        if (s === "KTB") return "KTB";
        if (s === "LHB") return "LHB";
        if (s === "SCB") return "SCB";
        if (s === "TTB") return "TTB";
        if (s === "UOB") return "UOB";
        return s;
      };

      setFormData({
        Invoice_No: invoice.Invoice_No && invoice.Invoice_No !== "-" ? invoice.Invoice_No : "",
        Paid_Amount: invoice.Paid && invoice.Paid > 0 ? invoice.Paid : invoice.ToPay || "",
        Paid_Date: paidDate,
        Remark: invoice.Remark && invoice.Remark !== "-" ? invoice.Remark : "",
        Payment_Method: invoice.Payment_Method ? String(invoice.Payment_Method) : "1",
        Ref1: invoice.Ref1 && invoice.Ref1 !== "-" ? invoice.Ref1 : "",
        Ref2: invoice.Ref2 && invoice.Ref2 !== "-" ? invoice.Ref2 : "",
        Withholding_Tax: invoice.Withholding_Tax === 1 || invoice.Withholding_Tax === true,
        Bank: normalizeBankCode(invoice.Bank),
        Check_No: invoice.Check_No && invoice.Check_No !== "-" ? invoice.Check_No : "",
        Transfer_Bank: normalizeBankCode(invoice.Transfer_Bank),
        Transfer_Account: invoice.Transfer_Account && invoice.Transfer_Account !== "-" ? invoice.Transfer_Account : "",
        Card_Bank: normalizeBankCode(invoice.Card_Bank),
        Card_No: invoice.Card_No && invoice.Card_No !== "-" ? invoice.Card_No : "",
        Check_Date: (() => {
          if (!invoice.Check_Date || invoice.Check_Date === "-") return "";
          const parts = invoice.Check_Date.split("/");
          if (parts.length === 3) {
            let y = parseInt(parts[2], 10);
            if (y > 2500) y -= 543;
            const m = moment(`${y}-${parts[1]}-${parts[0]}`, "YYYY-MM-DD");
            return m.isValid() ? m.format("YYYY-MM-DD") : "";
          }
          return "";
        })(),
      });
      setUserName(localStorage.getItem("name") || "");
      setFilesToDelete([]); // Reset delete list when modal opens
      setPaidAmountError(""); // Reset error when modal opens
      loadAttachedFiles();
    } else {
      setFilesToDelete([]); // Reset delete list when modal closes
      setPaidAmountError(""); // Reset error when modal closes
    }
  }, [isOpen, invoice]);

  useEffect(() => {
    if (!isOpen) return;
    const loadBankMasterOptions = async () => {
      try {
        const res = await api.get("/bank-master-options");
        if (res.data?.status === "OK" && Array.isArray(res.data.result) && res.data.result.length > 0) {
          const opts = res.data.result
            .map((r) => {
              const code = String(r.Bank_Code || "").trim().toUpperCase();
              const nameTh = String(r.B_Name_TH || "").trim();
              if (!code) return null;
              return { code, name: nameTh ? `${nameTh} (${code})` : code };
            })
            .filter(Boolean);
          if (opts.length > 0) setBankOptions(opts);
        }
      } catch (err) {
        console.error("Error loading bank master options:", err);
      }
    };
    loadBankMasterOptions();
  }, [isOpen]);

  const loadAttachedFiles = async () => {
    if (!invoice?.CT_Pay_Id) return;
    try {
      const res = await api.get(`/get-invoice-files/${invoice.CT_Pay_Id}`);
      if (res.data.status === "OK") {
        // แปลงข้อมูลจาก API format เป็น format ที่ใช้ใน component
        const files = (res.data.result || []).map((f) => ({
          File_Id: f.File_Id,
          File_Url: f.File_Url,
          File_Name: f.File_Name,
          name: f.File_Name,
          displayName: f.File_Name,
          type: f.File_Type || "",
          size: f.File_Size || 0,
          isNew: false,
        }));
        setSelectedFiles(files);
      }
    } catch (err) {
      console.error("Error loading files:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    // react-datepicker ส่งกลับมาเป็น Date object
    if (date) {
      const m = moment(date);
      if (m.isValid()) {
        // เก็บเป็น ค.ศ. (YYYY-MM-DD)
        setFormData((prev) => ({
          ...prev,
          Paid_Date: m.format("YYYY-MM-DD"),
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        Paid_Date: "",
      }));
    }
  };

  const handlePaidAmountChange = (value) => {
    // NumberInput ส่งกลับมาเป็น number แล้ว
    setFormData((prev) => ({
      ...prev,
      Paid_Amount: value,
    }));

    // ตรวจสอบ validation แบบ real-time
    const toPay = Number(invoice?.ToPay || 0);
    if (value > toPay) {
      setPaidAmountError(`ยอดชำระไม่สามารถเกินยอดที่ต้องชำระ (${toPay.toLocaleString()} บาท)`);
    } else {
      setPaidAmountError("");
    }
  };

  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const filePromises = files.map((file, index) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            id: Date.now() + index + Math.random(),
            name: file.name,
            type: file.type,
            size: file.size,
            file: file,
            dataUrl: e.target.result,
            isPending: true,
            displayName: file.name,
            isNew: true,
            rawFile: file,
          });
        };
        reader.onerror = () => {
          resolve({
            id: Date.now() + index + Math.random(),
            name: file.name,
            type: file.type,
            size: file.size,
            file: file,
            dataUrl: URL.createObjectURL(file),
            isPending: true,
            displayName: file.name,
            useObjectUrl: true,
            isNew: true,
            rawFile: file,
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(filePromises).then((processedFiles) => {
      setSelectedFiles((prev) => [...prev, ...processedFiles]);
    });

    event.target.value = "";
  };

  const handleRemoveImage = (index) => {
    const file = selectedFiles[index];
    
    // ถ้าเป็นไฟล์จาก API (มี File_Id) ให้เพิ่มเข้า list ที่ต้องการลบ (รอให้กดบันทึก)
    if (file.File_Id && !file.isNew) {
      setFilesToDelete((prev) => [...prev, file.File_Id]);
    }
    
    // ลบจาก selectedFiles (ไม่ลบจาก server ทันที)
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePreviewFile = (file) => {
    // สำหรับไฟล์ที่ upload ใหม่ (มี dataUrl)
    if (file.dataUrl) {
      window.open(file.dataUrl, "_blank");
      return;
    }
    
    // สำหรับไฟล์จาก API (มี File_Url)
    if (file.File_Url) {
      const url = file.File_Url.startsWith("http") 
        ? file.File_Url 
        : `${window.location.origin}/${file.File_Url}`;
      window.open(url, "_blank");
      return;
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      let url, fileName;
      
      // สำหรับไฟล์ที่ upload ใหม่
      if (file.dataUrl && file.rawFile) {
        const blob = await fetch(file.dataUrl).then(r => r.blob());
        url = window.URL.createObjectURL(blob);
        fileName = file.name || file.displayName || "download";
      }
      // สำหรับไฟล์จาก API
      else if (file.File_Url) {
        const fileUrl = file.File_Url.startsWith("http") 
          ? file.File_Url 
          : `${window.location.origin}/${file.File_Url}`;
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        url = window.URL.createObjectURL(blob);
        fileName = file.File_Name || file.name || "download";
      } else {
        Swal.fire("ผิดพลาด", "ไม่พบข้อมูลไฟล์", "error");
        return;
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error", err);
      Swal.fire("ผิดพลาด", "ไม่สามารถดาวน์โหลดไฟล์ได้", "error");
    }
  };

  const handleInputFileChange = (e) => {
    // Handle file change if needed
  };

  const handleBeforeUploadAttach = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf,.doc,.docx,.xls,.xlsx";
    input.multiple = true;
    input.onchange = (event) => {
      if (event.target.files.length > 3) {
        Swal.fire("ผิดพลาด", "สามารถอัปโหลดได้ไม่เกิน 3 ไฟล์ต่อครั้ง", "error");
        return;
      }
      handleFileSelection(event);
    };
    input.click();
    setOpenUploadAttach(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.Invoice_No || formData.Invoice_No.trim() === "") {
      Swal.fire("ผิดพลาด", "กรุณากรอกเลขใบแจ้งหนี้", "error");
      return;
    }

    const paidAmount = parseFloat(formData.Paid_Amount);
    if (isNaN(paidAmount) || paidAmount <= 0) {
      Swal.fire("ผิดพลาด", "ยอดชำระจริงต้องมากกว่า 0", "error");
      return;
    }

    // ตรวจสอบว่ายอดชำระไม่เกินยอดที่ต้องชำระในแต่ละงวด
    const toPay = Number(invoice?.ToPay || 0);
    if (paidAmount > toPay) {
      setPaidAmountError(`ยอดชำระไม่สามารถเกินยอดที่ต้องชำระ (${toPay.toLocaleString()} บาท)`);
      Swal.fire("ผิดพลาด", `ยอดชำระไม่สามารถเกินยอดที่ต้องชำระ (${toPay.toLocaleString()} บาท)`, "error");
      return;
    }

    // ตรวจสอบว่ายังมี error หรือไม่
    if (paidAmountError) {
      return;
    }

    try {
      setLoading(true);

      // Upload files first (if any)
      const newFiles = selectedFiles.filter((f) => f.isNew && f.rawFile);
      if (newFiles.length > 0) {
        const formDataFiles = new FormData();
        newFiles.forEach((f) => formDataFiles.append("files", f.rawFile));
        formDataFiles.append("Comp_Id", localStorage.getItem("company") || 1);
        formDataFiles.append("Upload_By", localStorage.getItem("name") || "System");

        const uploadRes = await api.post(`/upload-invoice-file/${invoice.CT_Pay_Id}`, formDataFiles, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (uploadRes.data.status !== "OK") {
          throw new Error(uploadRes.data.message || "อัปโหลดไฟล์ไม่สำเร็จ");
        }
      }

      // Delete files (if any)
      if (filesToDelete.length > 0) {
        for (const fileId of filesToDelete) {
          try {
            const deleteRes = await api.delete(`/delete-invoice-file/${fileId}`);
            if (deleteRes.data.status !== "OK") {
              console.error("Error deleting file:", deleteRes.data.message);
              // Continue with other deletions even if one fails
            }
          } catch (err) {
            console.error("Error deleting file:", err);
            // Continue with other deletions even if one fails
          }
        }
        setFilesToDelete([]); // Clear the delete list after successful deletion
      }

      // Update invoice
      const updateRes = await api.put(`/update-contract-invoice/${invoice.CT_Pay_Id}`, {
        Invoice_No: formData.Invoice_No.trim(),
        Paid_Amount: paidAmount,
        Paid_Date: formData.Paid_Date || null,
        Remark: formData.Remark || "",
        Payment_Method: formData.Payment_Method || "1",
        Ref1: formData.Ref1 || "",
        Ref2: formData.Ref2 || "",
        Withholding_Tax: formData.Withholding_Tax ? 1 : 0,
        Bank: formData.Payment_Method === "2" ? formData.Bank : "",
        Check_No: formData.Payment_Method === "2" ? formData.Check_No : "",
        Check_Date: formData.Payment_Method === "2" ? formData.Check_Date || null : null,
        Transfer_Bank: formData.Payment_Method === "3" ? formData.Transfer_Bank : "",
        Transfer_Account: formData.Payment_Method === "3" ? formData.Transfer_Account : "",
        Card_Bank: formData.Payment_Method === "4" ? formData.Card_Bank : "",
        Card_No: formData.Payment_Method === "4" ? formData.Card_No : "",
      });

      if (updateRes.data.status !== "OK") {
        throw new Error(updateRes.data.message || "อัปเดตข้อมูลไม่สำเร็จ");
      }

      Swal.fire({
        icon: "success",
        title: "สำเร็จ!",
        text: "บันทึกข้อมูลเรียบร้อยแล้ว",
        timer: 1500,
        showConfirmButton: false,
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error updating invoice:", err);
      Swal.fire("ผิดพลาด", err.message || "ไม่สามารถบันทึกข้อมูลได้", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9555]">
      <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg relative max-h-[95vh] overflow-y-auto" style={{ overflow: 'visible' }}>
        {/* Header */}
        <div className="flex items-center gap-2 px-6 py-4 bg-white sticky top-0 z-[1111] border-b">
          <Icon icon="mdi:file-document-edit" className="text-[#E13F57] w-6 h-6" />
          <h3 className="font-bold text-lg text-[#E13F57]">แก้ไขใบแจ้งหนี้</h3>
          <button
            onClick={onClose}
            className="ml-auto text-gray-500 hover:text-gray-700 text-xl px-2"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* เลขใบแจ้งหนี้ */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              เลขใบแจ้งหนี้ <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="Invoice_No"
              value={formData.Invoice_No}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 h-[42px]"
              placeholder="กรอกเลขใบแจ้งหนี้"
              required
            />
          </div>

          {/* ประเภทการชำระเงิน */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
              ประเภทการชำระเงิน <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-nowrap gap-x-6">
              {[
                { value: "1", label: "เงินสด" },
                { value: "2", label: "เช็ค" },
                { value: "3", label: "เงินโอน" },
                { value: "4", label: "บัตรเครดิต" },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 whitespace-nowrap shrink-0">
                  <input
                    type="radio"
                    name="Payment_Method"
                    value={opt.value}
                    checked={formData.Payment_Method === opt.value}
                    onChange={handleChange}
                    className="accent-[#E13F57] w-4 h-4"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* ธนาคาร + วันที่ออกเช็ค + เลขที่เช็ค (แสดงเฉพาะเมื่อเลือก เช็ค) */}
          {formData.Payment_Method === "2" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    ธนาคาร
                  </label>
                  <select
                    name="Bank"
                    value={formData.Bank}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 h-[42px] text-sm bg-white"
                  >
                    <option value="">-- เลือกธนาคาร --</option>
                    {bankOptions.map((b) => (
                      <option key={`cheque-${b.code}`} value={b.code}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    วันที่ออกเช็ค
                  </label>
                  <DatePicker
                    selected={
                      formData.Check_Date
                        ? (() => {
                            const m = moment(formData.Check_Date, "YYYY-MM-DD");
                            return m.isValid() ? m.toDate() : null;
                          })()
                        : null
                    }
                    onChange={(date) => {
                      if (date) {
                        const m = moment(date);
                        if (m.isValid()) {
                          setFormData((prev) => ({ ...prev, Check_Date: m.format("YYYY-MM-DD") }));
                        }
                      } else {
                        setFormData((prev) => ({ ...prev, Check_Date: "" }));
                      }
                    }}
                    dateFormat="dd/MM/yyyy"
                    locale="th"
                    placeholderText="เลือกวันที่"
                    className="w-full border rounded px-3 py-2 h-[42px]"
                    wrapperClassName="w-full"
                    customInput={<ThaiDateInput className="w-full border rounded px-3 py-2 h-[42px]" />}
                    renderCustomHeader={thaiDateHeader}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  เลขที่เช็ค
                </label>
                <input
                  type="text"
                  name="Check_No"
                  value={formData.Check_No}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                  placeholder="กรอกเลขที่เช็ค"
                />
              </div>
            </div>
          )}

          {/* ธนาคาร + เลขบัญชีที่รับเข้า (แสดงเฉพาะเมื่อเลือก เงินโอน) */}
          {formData.Payment_Method === "3" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                  ธนาคารที่รับเงิน
                </label>
                <select
                  name="Transfer_Bank"
                  value={formData.Transfer_Bank}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px] text-sm bg-white"
                >
                  <option value="">-- เลือกธนาคาร --</option>
                  {bankOptions.map((b) => (
                    <option key={`transfer-${b.code}`} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                  เลขบัญชีที่รับเข้า <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="Transfer_Account"
                  value={formData.Transfer_Account}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                  placeholder="กรอกเลขบัญชี"
                />
              </div>
            </div>
          )}

          {/* ธนาคาร + เลขบัตร (แสดงเฉพาะเมื่อเลือก บัตรเครดิต) */}
          {formData.Payment_Method === "4" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                  ธนาคารเจ้าของบัตร
                </label>
                <select
                  name="Card_Bank"
                  value={formData.Card_Bank}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px] text-sm bg-white"
                >
                  <option value="">-- เลือกธนาคาร --</option>
                  {bankOptions.map((b) => (
                    <option key={`card-${b.code}`} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
                  เลขบัตร
                </label>
                <input
                  type="text"
                  name="Card_No"
                  value={formData.Card_No}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2 h-[42px]"
                  placeholder="กรอกเลขบัตร"
                  maxLength={19}
                />
              </div>
            </div>
          )}

          {/* วันที่ชำระและยอดชำระ */}
          <div className="grid grid-cols-2 gap-4">
            {/* วันที่ชำระ */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                วันที่ชำระ
              </label>
              <DatePicker
                selected={
                  formData.Paid_Date
                    ? (() => {
                        const m = moment(formData.Paid_Date, "YYYY-MM-DD");
                        return m.isValid() ? m.toDate() : null;
                      })()
                    : null
                }
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                locale="th"
                placeholderText="เลือกวันที่"
                className="w-full border rounded px-3 py-2 h-[42px]"
                wrapperClassName="w-full"
                customInput={<ThaiDateInput className="w-full border rounded px-3 py-2 h-[42px]" />}
                renderCustomHeader={thaiDateHeader}
              />
              <p className="text-xs text-gray-500 mt-1">
                สำหรับกรณีชำระย้อนหลัง
              </p>
            </div>

            {/* ยอดชำระจริง */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">
                  ยอดชำระ <span className="text-red-500">*</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700 whitespace-nowrap">
                  <input
                    type="checkbox"
                    name="Withholding_Tax"
                    checked={formData.Withholding_Tax}
                    onChange={(e) => setFormData((prev) => ({ ...prev, Withholding_Tax: e.target.checked }))}
                    className="accent-[#E13F57] w-4 h-4"
                  />
                  หัก ณ ที่จ่าย
                </label>
              </div>
              <NumberInput
                value={formData.Paid_Amount}
                onValueChange={handlePaidAmountChange}
                disabled={false}
              />
              <p className="text-xs text-gray-500 mt-1">
                ยอดที่ต้องชำระ: {Number(invoice?.ToPay || 0).toLocaleString()} บาท
              </p>
              {paidAmountError && (
                <p className="text-xs text-red-600 mt-1">
                  {paidAmountError}
                </p>
              )}
              {!paidAmountError && (
                <p className="text-xs text-orange-600 mt-1">
                  * ยอดชำระต้องไม่เกินยอดที่ต้องชำระในแต่ละงวด
                </p>
              )}
            </div>
          </div>

          {/* Ref1 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Ref1</label>
            <input
              type="text"
              name="Ref1"
              value={formData.Ref1}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 h-[42px]"
              placeholder="Ref1"
            />
          </div>

          {/* Ref2 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Ref2</label>
            <input
              type="text"
              name="Ref2"
              value={formData.Ref2}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 h-[42px]"
              placeholder="Ref2"
            />
          </div>

          {/* ข้อมูลผู้ทำรายการ */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ผู้ทำรายการ
            </label>
            <input
              type="text"
              value={userName}
              className="w-full border rounded px-3 py-2 h-[42px] bg-gray-50"
              readOnly
              disabled
            />
          </div>

          {/* หมายเหตุ */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              หมายเหตุ
            </label>
            <textarea
              name="Remark"
              value={formData.Remark}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              rows="3"
              placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
            />
          </div>

          {/* แนบไฟล์ */}
          <div>
            <div className="flex items-center flex-nowrap gap-2 mb-2">
              <Icon
                icon="mdi:paperclip"
                className="w-5 h-5 text-gray-700 cursor-pointer hover:text-[#E13F57] transition-colors"
                onClick={handleBeforeUploadAttach}
                title="อัปโหลดไฟล์"
              />
              <label className="block text-sm font-semibold text-gray-700 cursor-pointer whitespace-nowrap" onClick={handleBeforeUploadAttach}>
                แนบไฟล์ (สลิปการโอนเงิน ฯลฯ)
              </label>
            </div>
            <FileModal
              mode="E"
              openUploadAttach={openUploadAttach}
              setOpenUploadAttach={setOpenUploadAttach}
              showViewAttachModal={showViewAttachModal}
              setShowViewAttachModal={setShowViewAttachModal}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              handleRemoveImage={handleRemoveImage}
              handleInputFileChange={handleInputFileChange}
              handleBeforeUploadAttach={handleBeforeUploadAttach}
            />
            
            {/* Preview uploaded files */}
            {selectedFiles.length > 0 && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0 fw-semibold">
                    <i className="fa fa-folder me-2"></i>ไฟล์ที่เลือก ({selectedFiles.length} ไฟล์)
                  </h6>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                    gap: "15px",
                  }}
                >
                  {selectedFiles.map((file, index) => {
                    // หา URL และชื่อไฟล์
                    const fileUrl = file.dataUrl || file.File_Url;
                    const fileName = file.name || file.displayName || file.File_Name || `ไฟล์ ${index + 1}`;
                    const fileType = file.type || (file.File_Url ? 'application/octet-stream' : '');
                    const isImage = fileType.startsWith('image/') || 
                      (file.File_Url && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.File_Url));
                    const previewUrl = file.dataUrl || 
                      (file.File_Url ? (file.File_Url.startsWith('http') ? file.File_Url : `${window.location.origin}/${file.File_Url}`) : null);

                    return (
                      <div
                        key={file.id || file.File_Id || index}
                        className="border rounded-lg shadow-sm text-center p-3 position-relative bg-white"
                      >
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="btn btn-sm btn-danger position-absolute top-0 start-0 m-2 rounded-circle"
                          style={{
                            width: "24px",
                            height: "24px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                          }}
                          title="ลบไฟล์"
                        >
                          <i className="fa fa-times"></i>
                        </button>

                        <div
                          className="d-flex align-items-center justify-content-center bg-light rounded mb-2"
                          style={{
                            height: "70px",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          onClick={() => handlePreviewFile(file)}
                        >
                          {isImage && previewUrl ? (
                            <img
                              src={previewUrl}
                              alt={fileName}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "cover",
                                borderRadius: "6px",
                              }}
                            />
                          ) : (
                            <i
                              className="fa fa-file"
                              style={{ fontSize: "36px", color: "#666" }}
                            ></i>
                          )}
                        </div>

                        <div
                          className="fw-semibold small text-truncate mb-1"
                          title={fileName}
                        >
                          {fileName}
                        </div>

                        {file.size && (
                          <div className="text-muted small mb-2">
                            {(() => {
                              if (!file.size) return "0 KB";
                              const kb = file.size / 1024;
                              return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
                            })()}
                          </div>
                        )}

                        <div className="d-flex justify-content-center gap-2">
                          <button
                            className="btn btn-sm"
                            style={{
                              backgroundColor: "rgb(239, 108, 0)",
                              color: "white",
                              padding: "4px 8px",
                              fontSize: "11px",
                              borderRadius: "6px",
                              border: "none",
                            }}
                            onClick={() => handlePreviewFile(file)}
                          >
                            <i className="fa fa-eye"></i> ดู
                          </button>

                          <button
                            className="btn btn-sm"
                            style={{
                              backgroundColor: "#28a745",
                              color: "white",
                              padding: "4px 8px",
                              fontSize: "11px",
                              borderRadius: "6px",
                              border: "none",
                            }}
                            onClick={() => handleDownloadFile(file)}
                          >
                            <i className="fa fa-download"></i> โหลด
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-[#E13F57] text-white rounded hover:bg-[#c9354a] disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "กำลังบันทึก..." : "บันทึก"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

